import { PayloadAction } from '@reduxjs/toolkit';
import { ChatArchive, ChatShortcut } from 'generated/api';
import { api } from 'careand-redux/api/ApiThunks';
import { createResettableSlice } from 'careand-redux/utils/makeResettable';
import { unwrap, unwrapApi } from 'careand-redux/utils/unwrap';
import PerformanceUtils from 'services/PerformanceUtils';

export interface ChatState {
  activeLinkedAccount: Record<string, string | undefined>;
  archives: Record<string, ChatArchive[] | undefined>;
  chatRooms: ChatArchive[];
  shortcuts: ChatShortcut[];
  unreadCount: Partial<Record<string, number>>;
}

export interface ReduxChatMessage {
  id: string;
  time: number;
  author: string;
  message: string;
  attributes?: Record<string, unknown>; // TODO
}

const initialState: ChatState = {
  activeLinkedAccount: {},
  archives: {},
  chatRooms: [],
  shortcuts: [],
  unreadCount: {},
};

const slice = createResettableSlice({
  name: 'Chat',
  initialState,
  reducers: {
    receiveChatRoom: (state: ChatState, action: PayloadAction<ChatArchive>) => {
      if (!action.payload.is_active) {
        PerformanceUtils.removeFromArrayInPlace(action.payload, state.chatRooms, 'id');
      } else {
        editOrAddChatRoom(state, action.payload);
      }
    },
    removeChatRoom: (
      state: ChatState,
      action: PayloadAction<{ room: ChatArchive; archive: ChatArchive }>,
    ) => {
      const { room, archive } = action.payload;
      if (room) {
        PerformanceUtils.removeFromArrayInPlace(room, state.chatRooms, 'id');
        if (archive?.patient_id) {
          state.archives[archive.patient_id] = PerformanceUtils.upsert(
            archive,
            state.archives[archive.patient_id] ?? [],
          );
        }
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(
        api.Chat.getUserArchives.fulfilled,
        unwrapApi((state, archives, req) => {
          state.archives[req.userId] = archives;
        }),
      )
      .addCase(
        api.Chat.addChatShortcut.fulfilled,
        unwrapApi((state, shortcut: ChatShortcut) => {
          PerformanceUtils.mutableEditOrAdd(shortcut, state.shortcuts);
        }),
      )
      .addCase(
        api.Chat.getChatShortcuts.fulfilled,
        unwrapApi((state, shortcuts: ChatShortcut[]) => {
          state.shortcuts = shortcuts;
        }),
      )

      .addCase(api.Chat.createHelpdeskChatRoom.fulfilled, unwrapApi(editOrAddChatRoom))
      .addCase(api.Chat.createOnDemandChatRoom.fulfilled, unwrapApi(editOrAddChatRoom))
      .addCase(api.Chat.claimChatRoom.fulfilled, unwrapApi(editOrAddChatRoom))
      .addCase(
        api.Chat.archiveChatRoom.fulfilled,
        unwrapApi((state, payload, req) => {
          PerformanceUtils.removeFromArrayInPlace({ id: req.chatId }, state.chatRooms, 'id');
        }),
      )
      .addCase(
        api.Chat.getChatRooms.fulfilled,
        unwrapApi((state, chats) => {
          state.chatRooms = chats;
        }),
      )
      .addCase(
        api.Chat.getUnreadCount.fulfilled,
        unwrap((state, data) => {
          if (!data.id) {
            return;
          }
          state.unreadCount[data.id] = data.count || 0;
        }),
      )
      .addCase(
        api.Chat.updateUnreadCount.fulfilled,
        unwrapApi((state, data, req) => {
          if (!req.userId) {
            return;
          }
          state.unreadCount[req.userId] = req.integerResource.value || 0;
        }),
      )
      .addCase(
        api.HealthRecords.countUnreadHealthRecords.fulfilled,
        unwrapApi((state, data, req) => {
          data.forEach((it) => it.id && (state.unreadCount[it.id] = it.chat_count));
        }),
      ),
});

function editOrAddChatRoom(state: ChatState, chat: ChatArchive) {
  PerformanceUtils.upsert(chat, state.chatRooms, 'id');
}

export const chatActions = slice.actions;
export default slice.reducer;
